/* eslint-disable react/jsx-filename-extension */
/* eslint-disable react/prop-types */
/* eslint-disable react/jsx-no-constructed-context-values */
import React, {
  createContext, useState, useEffect, useRef,
} from 'react';
import { useRouter } from 'next/router';

// Create the navigation context
export const NavigationContext = createContext();

/**
 * NavigationProvider - Tracks navigation history and previous page state
 *
 * This provider maintains:
 * - previousPath: The previous page's pathname
 * - previousQuery: The previous page's query parameters
 * - previousAsPath: The previous page's full path (including query)
 * - currentPath: Current page pathname
 * - navigationHistory: Array of recent navigation paths (limited to last 10)
 * - customState: Optional custom state that can be set programmatically
 * - scrollPositions: Map of paths to their scroll positions (auto-restores on navigation)
 */
export default function NavigationProvider({ children }) {
  const router = useRouter();
  const isFirstRender = useRef(true);
  const scrollPositions = useRef({}); // Store scroll positions in ref to avoid re-renders
  const pendingScrollPath = useRef(null); // Track the path we're navigating to

  const [navigationState, setNavigationState] = useState({
    previousPath: null,
    previousQuery: null,
    previousAsPath: null,
    currentPath: router.pathname,
    navigationHistory: [],
    customState: null,
  });

  // Disable Next.js automatic scroll restoration
  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  // Track route changes
  useEffect(() => {
    const handleRouteChangeStart = (url) => {
      // Save current scroll position before navigating away
      const currentPath = router.asPath;
      const { scrollY } = window;
      scrollPositions.current[currentPath] = scrollY;

      // Strip basePath prefix if present and store destination
      const basePath = '/prep';
      const cleanUrl = url.startsWith(basePath) ? url.slice(basePath.length) : url;
      pendingScrollPath.current = cleanUrl || '/';

      // Skip navigation history tracking on first render
      if (isFirstRender.current) {
        isFirstRender.current = false;
        return;
      }

      setNavigationState((prev) => {
        // Add current path to history (limit to last 10 entries)
        const newHistory = [
          ...prev.navigationHistory.slice(-9),
          {
            pathname: router.pathname,
            query: router.query,
            asPath: router.asPath,
            timestamp: new Date().toISOString(),
          },
        ];

        return {
          ...prev,
          previousPath: router.pathname,
          previousQuery: router.query,
          previousAsPath: router.asPath,
          currentPath: url.split('?')[0], // Extract pathname from URL
          navigationHistory: newHistory,
        };
      });
    };

    // Progressive scroll restoration for infinite scroll pages
    const restoreScrollPosition = (targetPosition) => {
      const maxScroll = document.documentElement.scrollHeight - window.innerHeight;

      // Page is tall enough, just scroll directly
      if (targetPosition <= maxScroll) {
        window.scrollTo(0, targetPosition);
        return;
      }

      // Page isn't tall enough - progressively scroll to trigger infinite scroll loading
      let attempts = 0;
      const maxAttempts = 30;
      let lastHeight = document.documentElement.scrollHeight;
      let cancelled = false;

      // Stop scroll restoration if user manually scrolls
      const cancelRestoration = () => {
        cancelled = true;
        // eslint-disable-next-line no-use-before-define
        cleanup();
      };

      const cleanup = () => {
        window.removeEventListener('wheel', cancelRestoration);
        window.removeEventListener('touchmove', cancelRestoration);
        window.removeEventListener('mousedown', cancelRestoration);
      };

      window.addEventListener('wheel', cancelRestoration, { passive: true, once: true });
      window.addEventListener('touchmove', cancelRestoration, { passive: true, once: true });
      window.addEventListener('mousedown', cancelRestoration, { once: true });

      const tryScroll = () => {
        if (cancelled) {
          return;
        }

        const currentMaxScroll = document.documentElement.scrollHeight - window.innerHeight;

        // Check if we can now reach target position
        if (targetPosition <= currentMaxScroll) {
          window.scrollTo(0, targetPosition);
          cleanup();
          return;
        }

        // Scroll to current bottom to trigger infinite scroll
        window.scrollTo(0, currentMaxScroll);

        const currentHeight = document.documentElement.scrollHeight;
        const heightChanged = currentHeight !== lastHeight;
        lastHeight = currentHeight;

        attempts += 1;
        if (attempts < maxAttempts) {
          // Wait longer if height didn't change (waiting for content to load)
          const delay = heightChanged ? 100 : 300;
          setTimeout(tryScroll, delay);
        } else {
          cleanup();
        }
      };

      tryScroll();
    };

    const handleRouteChangeComplete = () => {
      setNavigationState((prev) => ({
        ...prev,
        currentPath: router.pathname,
      }));

      // Use the tracked destination path
      const newPath = pendingScrollPath.current;
      const savedPosition = scrollPositions.current[newPath];

      // Scroll restoration with support for infinite scroll pages
      setTimeout(() => {
        if (savedPosition !== undefined) {
          restoreScrollPosition(savedPosition);
        } else {
          window.scrollTo(0, 0);
        }
      }, 100);
    };

    // Subscribe to route change events
    router.events.on('routeChangeStart', handleRouteChangeStart);
    router.events.on('routeChangeComplete', handleRouteChangeComplete);

    return () => {
      router.events.off('routeChangeStart', handleRouteChangeStart);
      router.events.off('routeChangeComplete', handleRouteChangeComplete);
    };
  }, [router]);

  // Method to set custom state that persists during navigation
  const setCustomNavigationState = (state) => {
    setNavigationState((prev) => ({
      ...prev,
      customState: state,
    }));
  };

  // Method to clear custom state
  const clearCustomNavigationState = () => {
    setNavigationState((prev) => ({
      ...prev,
      customState: null,
    }));
  };

  // Method to navigate back to previous page
  const goToPreviousPage = () => {
    if (navigationState.previousAsPath) {
      router.push(navigationState.previousAsPath);
    } else {
      router.back();
    }
  };

  // Method to check if there's a previous page
  const hasPreviousPage = () => navigationState.previousPath !== null || navigationState.navigationHistory.length > 0;

  return (
    <NavigationContext.Provider
      value={{
        ...navigationState,
        setCustomNavigationState,
        clearCustomNavigationState,
        goToPreviousPage,
        hasPreviousPage,
      }}
    >
      {children}
    </NavigationContext.Provider>
  );
}
